import { SYSTEM_ROLES } from '../config/roles.config';
import { centToCurrency } from './formatNumber';

/**
 * Returns the linked business-entity name for the given role.
 *
 * Administrator is not linked to any business entity and returns an empty string.
 * Other roles return the name of their corresponding entity (merchant, provider, agent).
 *
 * @function getUserNameByRole
 * @param {Object} role - The role object.
 * @param {string} role.systemRole - System role identifier.
 * @param {Object} data - The data object representing the full row or entity.
 * @returns {string} The resolved entity name, or an empty string.
 *
 * @example
 * getUserNameByRole({ systemRole: 'merchant' }, { merchant: { name: 'John Doe' } }); // → "John Doe"
 * getUserNameByRole({ systemRole: 'admin' }, { merchant: { name: 'John Doe' } });    // → ""
 */
export const getUserNameByRole = (role, data) => {
    const map = {
        [SYSTEM_ROLES.MERCHANT]: data?.merchant?.name,
        [SYSTEM_ROLES.PSP]: data?.provider?.name,
        [SYSTEM_ROLES.AGENT]: data?.agent?.name
    };

    return map[role?.systemRole] ?? '';
};

/**
 * Retrieves the UUID of a transaction based on its kind.
 *
 * Supported kinds are:
 * - `'payment'`: returns `data.payment.uuid`
 * - `'p2p'`: returns `data.p2p.uuid`
 * - `'payout'`: returns `data.payout.uuid`
 *
 * If the kind does not match any of the supported transaction types,
 * an empty string is returned.
 *
 * @function getUuidTransactionByKind
 * @param {string} kind - The type of transaction (`'payment'` | `'p2p'` | `'payout'`).
 *                        This is a string identifying the transaction category.
 * @param {Object} data - The full transaction data object.
 * @param {Object} [data.payment] - Payment transaction data.
 * @param {string} [data.payment.uuid] - UUID of the payment transaction.
 * @param {Object} [data.p2p] - P2P transaction data.
 * @param {string} [data.p2p.uuid] - UUID of the P2P transaction.
 * @param {Object} [data.payout] - Payout transaction data.
 * @param {string} [data.payout.uuid] - UUID of the payout transaction.
 * @returns {string} The UUID string corresponding to the specified kind, or an empty string if unavailable.
 *
 * @example
 * getUuidTransactionByKind('payment', transactionData); // → "a1b2c3d4..."
 * getUuidTransactionByKind('p2p', transactionData);     // → "d4c3b2a1..."
 * getUuidTransactionByKind('invalid', transactionData); // → ""
 */
export const getUuidTransactionByKind = (kind, data) => {
    const map = {
        payment: data?.payment?.uuid,
        p2p: data?.p2p?.uuid,
        payout: data?.payout?.uuid
    };
    return map[kind] ?? '';
};

export const getCostType = (type, data) => {
    const agentDisplay = [
        data?.agent?.name ? `agent: ${data.agent.name}` : null,
        data?.gateway?.name ? `gateway: ${data.gateway.name}` : null,
        data?.terminal?.name ? `terminal: ${data.terminal.name}` : null,
    ]
        .filter(Boolean)
        .join('; ');


    const map = {
        merchant: data?.terminal?.name,
        gateway: data?.gateway?.name,
        agent: agentDisplay,
    };
    return map[type] ?? '';
};

export const getTotalCommission = (id, data) => {
    return data[id];
};

export const getFeeByDimension = (dimension, data) => {
    if (dimension === 'fixed') {
        return centToCurrency(data.fee);
    } else if (dimension === 'percent') {
        return data?.fee ? `${data.fee}%` : '';
    }
};
